import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, Check, X, Undo2 } from "lucide-react";
import { playReadingAudio } from "@/lib/readingAudio";

const PIC_TYPES = ["LISTEN_PICTURE", "ODD_ONE_OUT", "READ_MATCH"];

export function Pic({ k, label, emoji, className = "" }) {
  const [bad, setBad] = useState(false);
  if (!k || bad) {
    return (
      <div className={`flex items-center justify-center text-center font-bold ${className}`} data-testid="reading-pic-fallback">
        {emoji ? <span className="text-6xl">{emoji}</span> : <span className="text-lg leading-tight px-2">{label}</span>}
      </div>
    );
  }
  return <img src={`/reading/pics/${k}.webp?v=2`} alt={label || ""} onError={() => setBad(true)} draggable={false}
    className={`object-contain select-none ${className}`} />;
}

export function SpeakerBtn({ onPlay, big, testid = "reading-speaker" }) {
  const [on, setOn] = useState(false);
  const play = async (e) => { e?.stopPropagation(); setOn(true); await onPlay(); setOn(false); };
  return (
    <motion.button onClick={play} whileTap={{ scale: 0.9 }} data-testid={testid} aria-label="Listen"
      animate={on ? { scale: [1, 1.12, 1] } : {}} transition={on ? { repeat: Infinity, duration: 0.8 } : {}}
      className={`rounded-full bg-yellow-300 border-[3px] border-slate-900 shadow-[4px_4px_0_0_#0F172A] flex items-center justify-center shrink-0 ${big ? "w-24 h-24" : "w-12 h-12"}`}>
      <Volume2 size={big ? 44 : 22} />
    </motion.button>
  );
}

function BuildWord({ ex, onAnswer, locked }) {
  const tiles = useMemo(() => String(ex.shown_text || "").split(/\s+/).filter(Boolean).map((t, i) => ({ t, i })), [ex.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [placed, setPlaced] = useState([]);
  useEffect(() => setPlaced([]), [ex.id]);
  useEffect(() => { if (placed.length === tiles.length && tiles.length) onAnswer(placed.map((p) => p.t).join("")); }, [placed]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="flex flex-col items-center gap-6" data-testid="reading-build-word">
      <div className="flex gap-3">
        {tiles.map((_, i) => (
          <button key={i} onClick={() => !locked && setPlaced(placed.filter((_, j) => j !== i))} data-testid={`build-slot-${i}`}
            className="w-16 h-20 sm:w-20 sm:h-24 rounded-2xl border-[3px] border-dashed border-slate-400 bg-white flex items-center justify-center text-4xl sm:text-5xl font-bold">
            {placed[i] && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }}>{placed[i].t}</motion.span>}
          </button>
        ))}
      </div>
      <div className="flex gap-3">
        {tiles.map((p) => {
          const used = placed.some((x) => x.i === p.i);
          return (
            <motion.button key={p.i} whileTap={{ scale: 0.9 }} disabled={used || locked} onClick={() => setPlaced([...placed, p])}
              data-testid={`build-tile-${p.i}`}
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-[3px] border-slate-900 text-4xl font-bold shadow-[4px_4px_0_0_#0F172A] transition-opacity ${used ? "opacity-20" : "bg-sky-200"}`}>
              {p.t}
            </motion.button>
          );
        })}
      </div>
      {placed.length > 0 && !locked && (
        <button onClick={() => setPlaced([])} className="text-sm font-bold text-slate-500 inline-flex items-center gap-1" data-testid="build-reset">
          <Undo2 size={16} /> Reset
        </button>
      )}
    </div>
  );
}

// One exercise screen; the parent decides right/wrong through onAnswer and passes back the state.
export default function ReadingExercise({ ex, lang, petit, onAnswer, wrong = [], correct = null, reveal = null, locked }) {
  const picOpts = PIC_TYPES.includes(ex.type) && Boolean(ex.option_pics?.length);
  const hearAudio = () => playReadingAudio(ex.audio, lang);
  const showText = ex.shown_text && ex.type !== "BUILD_WORD";
  const tile = (o, i) => {
    const isWrong = wrong.includes(o);
    const isRight = correct === o || reveal === o;
    const state = isRight ? "bg-green-300 border-green-700" : isWrong ? "bg-red-100 border-red-400 opacity-60" : "bg-white hover:bg-yellow-50";
    return (
      <motion.div key={o} role="button" tabIndex={0} aria-disabled={locked || isWrong}
        onClick={() => !locked && !isWrong && onAnswer(o)} onKeyDown={(e) => e.key === "Enter" && !locked && !isWrong && onAnswer(o)}
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0, x: isWrong ? [0, -8, 8, -4, 0] : 0 }} transition={{ delay: i * 0.06 }}
        whileTap={{ scale: 0.95 }} data-testid={`reading-option-${i}`}
        className={`relative cursor-pointer select-none rounded-3xl border-[3px] border-slate-900 shadow-[5px_5px_0_0_#0F172A] flex flex-col items-center justify-center transition-colors ${state} ${picOpts ? "aspect-square p-2" : petit ? "h-28 sm:h-32" : "h-20 sm:h-24"}`}>
        {picOpts ? (
          <Pic k={ex.option_pics?.[i]} label={o} className="w-full h-full" />
        ) : (
          <span className={`font-bold ${o.length > 6 ? "text-2xl sm:text-3xl" : petit ? "text-5xl sm:text-6xl" : "text-4xl sm:text-5xl"}`}>{o}</span>
        )}
        {ex.type === "ODD_ONE_OUT" && (
          <span className="absolute top-2 right-2"><SpeakerBtn onPlay={() => playReadingAudio(o, lang)} testid={`reading-option-speak-${i}`} /></span>
        )}
        {isRight && <Check className="absolute top-2 left-2 text-green-800" size={28} />}
        {isWrong && <X className="absolute top-2 left-2 text-red-600" size={28} />}
      </motion.div>
    );
  };
  return (
    <div className="flex flex-col items-center gap-6 w-full" data-testid={`reading-exercise-${ex.type}`}>
      <div className="flex items-center gap-5 flex-wrap justify-center">
        {ex.audio && ex.type !== "ODD_ONE_OUT" && <SpeakerBtn onPlay={hearAudio} big={petit} />}
        {ex.picture && (
          <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-3xl bg-white border-[3px] border-slate-900 p-2 shadow-[5px_5px_0_0_#0F172A]">
            <Pic k={ex.picture} label="" emoji="🖼️" className="w-full h-full" />
          </div>
        )}
        {showText && (
          <div className={`rounded-3xl bg-white border-[3px] border-slate-900 px-6 py-4 font-bold tracking-wide shadow-[5px_5px_0_0_#0F172A] ${ex.shown_text.length > 12 ? "text-3xl sm:text-4xl" : "text-5xl sm:text-6xl"}`} data-testid="reading-shown-text">
            {ex.shown_text}
          </div>
        )}
      </div>
      {ex.type === "BUILD_WORD" ? (
        <BuildWord ex={ex} onAnswer={onAnswer} locked={locked} />
      ) : (
        <div className={`grid gap-4 w-full ${picOpts ? "grid-cols-2 max-w-md sm:max-w-xl sm:grid-cols-4" : "grid-cols-2 max-w-lg"}`}>
          {ex.options.map(tile)}
        </div>
      )}
    </div>
  );
}
